import { ArchiveAtTypeEnum } from './archiveAtEnum';
import { AssignmentDefault } from './assignmentDefault';
import { CancelReason } from './cancelReason';
import { CertificateGroup } from './certificateGroup';
import { CustomCriteria } from './customCriteria';
import { CustomerTariffOptions } from './customerTarifs';
import { DemandReason } from './demandReason';
import { DemoOrganisationType } from './demoOrganisationType';
import { EmploymentTransferConfig } from './employmentTransferConfig';
import { InpoolLanguage } from './InpoolLanguage';
import { MongoDocument } from './mongoDocument';
import { NotificationEnum } from './notificationEnum';
import { PoolerType } from './poolerType';
import { ReportsEnum } from './reports.enum';
import { ShiftEnum } from './shift.enum';
import { Tag } from './tag';
import { ConditionalTransferOptions, Transfer } from './transfer';
import { MappedCertificate } from './user';
import { WeekDayEnum } from './weekday.enum';

// ANY CHANGES IN THIS MODEL WILL HAVE TO BE REFLECTED IN THE ADMIN CUSTOMER SCHEMA
export interface Customer extends MongoDocument {
  name: string;
  customerType: CustomerTypeEnum;
  enableJobs: boolean;
  enableEmployments: boolean;
  organisationIds?: string[];
  dispoIds: string[];
  requesterIds?: string[];
  branches?: { id: string; displayName: string }[];
  emails?: { types: NotificationEnum[]; email: string }[];
  phones?: { types: NotificationEnum[]; phone: string }[];
  pushUserIds?: { types: NotificationEnum[]; userId: string }[];
  locale: InpoolLanguage;
  fromEmail?: string;
  logo?: string;
  customerTransfers?: {
    confirmTransfers?: boolean;
    transferConfirmationPersons?: string[];
    transferConditions: {
      jobTransferConditions: ConditionalTransferOptions[];
      employmentTransferConditions: ConditionalTransferOptions[];
    };
    transfers: Transfer[];
    employmentTransfers: EmploymentTransferConfig[];
  };
  preventDirectAcceptWithinSecondsPerShift?: DurationPerShift;
  demoExpectedJobsAmount?: number;
  isDemo?: boolean;
  // we have 2 static transfer customers and one static shared pool customer for our demo
  // these do not get new jobs through cron, but new availabilities are generated for them
  isDemoStatic?: boolean;
  demoValidUntil?: Date;
  demoTypeOfOrganisation?: DemoOrganisationType;
  // Number used to differentiate multiple demos of the same customer
  demoInstanceNumber?: number;
  enableCustomerAvailabilityOverview?: boolean;
  enablePoolerDepartmentAvailabilityOverview?: boolean;
  enableUserPreferenceDepartmentBlacklist?: boolean;
  enableSupportPage?: boolean;
  enablePoolerDocuments?: boolean;
  enableManualCancelTimestampForExternal?: boolean;
  isExternal?: boolean;
  autoBookInvitees?: boolean;
  autoBookInviteesDepartmentDefault?: boolean;
  autoBookSelfSignIns?: boolean;
  autoBookSelfSignInsDepartmentDefault?: boolean;
  hiddenFields?: HiddenFields;
  visibleCertificates?: string[];
  visibleSpecialities?: string[];
  pool: Pool;
  createdUserIds: string[];
  disabled: boolean;
  // no reminders if undefined
  jobReminderSettings?: ReminderSettings;
  inviteReminderSettings?: ReminderSettings;
  enableInviteeTransfer?: boolean;
  // allow partial accepting of shift
  partialAccept?: boolean;
  entryExitDates?: boolean;
  disableDispoFilterSave?: boolean;
  dispoNoOwnEdit?: boolean;
  sharedPoolPriorities?: { customerId: string; customerName: string }[];
  preventSharedPoolDirectAcceptCustomerIds?: string[];
  requiredPause?: number;
  enableTags?: boolean;
  tags?: Tag[];
  basicAuthEncoded?: string;
  externalApiData?: {
    domains: string[];
    auth: string;
    oAuthClientId?: string;
    oAuthClientSecret?: string;
  };
  enablePoolerTypes?: boolean;
  poolerTypes?: PoolerType[];
  transferCancelCostPeriod?: number;
  poolerNotificationSettings?: {
    forceBookingSmsNotifications?: boolean;
    forceBookingEmailNotifications?: boolean;
    forceCancelSmsNotifications?: boolean;
    forceCancelEmailNotifications?: boolean;
    disabledSMS?: boolean;
  };
  hasJobAssignments?: boolean;
  hasEmployments?: boolean;
  enableInviteeFiles?: boolean;
  enableTariffs?: boolean;
  msalConfig?: MsalConfig;
  adfsConfig?: AdfsConfig;
  customerTariffs?: CustomerTariffOptions;
  enableDefaultValues?: boolean;
  defaultJobValues?: AssignmentDefault[];
  defaultEmploymentValues?: AssignmentDefault[];
  requireTwoFactorAuth?: boolean;
  twoFactorAuthIPWhiteList?: string[];
  twoFactorAuthIPWhiteListRanges?: { rangeStart: string; rangeEnd: string }[];
  requiredJobFields?: RequiredJobFields;
  requiredEmploymentFields?: RequiredEmploymentFields;
  externalPoolerOffByDefault?: boolean;
  enableDemandReasons?: boolean;
  demandReasons?: DemandReason[];
  enableInternalDescription?: boolean;
  enableJobRequestProcess?: boolean;
  enableJobRequestApproval?: boolean;
  requestersCanAccessReports?: boolean;
  requestersCanAccessJobOverviewReport?: boolean;
  enableCaseNumberInJobRequests?: boolean;
  enablePlanningMatrix?: boolean;
  cancelReasons?: CancelReason[];
  enableConditionalTransfers?: boolean;
  prioritizeEmployeeLastName?: boolean;
  transferredJobsNoLongerActionRequired?: boolean;
  enableCopyDateOnDuplicate?: boolean;
  hideContactInfoInJobRequest?: boolean;
  enableSpecialitiesForJobRequests?: boolean;
  enableAutoDirectAccept?: boolean;
  preventAutoDirectAcceptWithinHours?: {
    [ShiftEnum.EARLY]: number;
    [ShiftEnum.DAY]: number;
    [ShiftEnum.LATE]: number;
    [ShiftEnum.NIGHT]: number;
  };
  allowIgnoreUserPreferenceDepartmentBlacklist?: boolean;
  preventChangingOfCreator?: boolean;
  enableRequestOverview?: boolean;
  enableSectionToggleInJobMatrix?: boolean;
  jobRequestApprovalNotificationSettings?: JobRequestApprovalNotificationSettings[];
  enableMonthlyHours?: boolean;
  approvedJobRequestsNoLongerActionRequired?: boolean;
  certificateGroups?: CertificateGroup[];
  customCriteria?: CustomCriteria[];
  jobMatrixHideCanceledJobs?: boolean;
  jobMatrixCancelReasonExceptions?: CancelReason[];
  jobMatrixForRequester?: boolean;
  jobMatrixCertificatePriority?: {
    priority: number;
    certificates: MappedCertificate[];
  }[];
  enableTranscribeJobList?: boolean;
  bookingWebHook?: {
    url: string;
    apiKeyEncrypted: string;
    apiKeyPreview: string;
  };
  enablePerigonApi?: boolean;
  perigonMappings?: PerigonMappings;
  perigonBearerToken?: string;
  // time tracking settings
  timeTrackingEnabled?: boolean;
  timeTrackingDefaultPauseInHours?: {
    [ShiftEnum.EARLY]: number;
    [ShiftEnum.DAY]: number;
    [ShiftEnum.LATE]: number;
    [ShiftEnum.NIGHT]: number;
  };
  timeTrackingOrganisationBlacklist?: string[];
  timeTrackingDefaultPauseByDepartment?: {
    [departmentId: string]: {
      [ShiftEnum.EARLY]?: number;
      [ShiftEnum.DAY]?: number;
      [ShiftEnum.LATE]?: number;
      [ShiftEnum.NIGHT]?: number;
    };
  };
  timeTrackingDescriptionRequiredOnChange?: boolean;
  enableTimeTrackingSigninProcess?: boolean;
  disableTimetrackingForOwnPoolers?: boolean;
  disableTimetrackingForNewOrganisationsByDefault?: boolean;
  disableApproveTimetrackingNotificationsForTransfers?: boolean;
  timeTrackingAutoApproveInDays?: number;
  autoAddTimeTrackingHours?: number;
  sharedPoolAllowUndecidedCustomerIds?: string[];
  archiveAtType?: ArchiveAtTypeEnum;
  archiveAtOffset?: number;
  hiddenReports?: ReportsEnum[];
}
// can be set to send reminders if the job has been booked X seconds before the job started to remind the pooler X seconds before the job starts
export type ReminderSettings = {
  // send reminder if booking is within X seconds before the job starts
  // -> decides if a reminder is necessary for this job
  conditionSeconds: number;
  // send reminder X seconds before the job starts
  // -> decides when to send the reminder
  reminderSeconds: number;
  sendEmail: boolean;
  sendSms: boolean;
};

export type Pool = {
  jobsSharedWithCustomerIds: string[];
  employmentsSharedWithCustomerIds: string[];
  activeUserIds: string[];
  requestedUserIds: string[];
  registeredUserIds: string[];
  formerUserIds?: string[];
};

export type DurationPerShift = {
  [ShiftEnum.EARLY]: number;
  [ShiftEnum.DAY]: number;
  [ShiftEnum.LATE]: number;
  [ShiftEnum.NIGHT]: number;
};
export type DurationPerShiftOptional = {
  [ShiftEnum.EARLY]?: number;
  [ShiftEnum.DAY]?: number;
  [ShiftEnum.LATE]?: number;
  [ShiftEnum.NIGHT]?: number;
};

export type HiddenFields = {
  jobCaseNumber?: boolean;
  jobDescription?: boolean;
  jobSex?: boolean;
  jobSpecialities?: boolean;
  jobCertificates?: boolean;
  jobMobility?: boolean;
  jobLanguage?: boolean;
  jobPoolerTypes?: boolean;
  employmentDescription?: boolean;
  employmentSex?: boolean;
  employmentSpecialities?: boolean;
  employmentCertificates?: boolean;
  employmentMobility?: boolean;
  employmentLanguage?: boolean;
  profileMobility?: boolean;
  profileInternalId?: boolean;
  profileITKnowledge?: boolean;
  profileClothingSize?: boolean;
  profileHeight?: boolean;
  profileHourlyWage?: boolean;
  profileLanguages?: boolean;
  profileNotes?: boolean;
  profileDegrees?: boolean;
  profileDateOfBirth?: boolean;
  profilePlaceOfResidence?: boolean;
  jobtype?: boolean;
};

export enum CustomerTypeEnum {
  HOSPITAL = 'HOSPITAL',
  AGENCY = 'AGENCY',
}

export type RequiredEmploymentFields = {
  certificates?: boolean;
  sex?: boolean;
  dateOfBirth?: boolean;
};

export type RequiredJobFields = {
  certificates?: boolean;
  demandReason?: boolean;
  sex?: boolean;
  dateOfBirth?: boolean;
};

export interface JobRequestApprovalNotificationSettings {
  id: string;
  label: string;
  departmentIds?: string[];
  notificationType: 'email' | 'sms';
  target: string;
  weekdaySchedule: JobRequestWeekdayScheduleSettings;
}

export interface JobRequestWeekdayScheduleSettings {
  [WeekDayEnum.MONDAY]: JobRequestDaySchedule;
  [WeekDayEnum.TUESDAY]: JobRequestDaySchedule;
  [WeekDayEnum.WEDNESDAY]: JobRequestDaySchedule;
  [WeekDayEnum.THURSDAY]: JobRequestDaySchedule;
  [WeekDayEnum.FRIDAY]: JobRequestDaySchedule;
  [WeekDayEnum.SATURDAY]: JobRequestDaySchedule;
  [WeekDayEnum.SUNDAY]: JobRequestDaySchedule;
}

export interface MsalConfig {
  cloudInstance: string;
  tenantId: string;

  clientId: string;
  // Email domains allowed for MSAL authentication (must start with @)
  emailDomains?: string[];
}

export interface AdfsConfig {
  // ADFS server URL (e.g., "https://adfs.company.com")
  authority: string; // https://example.ch/adfs
  // Client ID registered in ADFS
  clientId: string;
  clientSecret: string;
  // Email domains allowed for ADFS authentication
  // "example.ch"
  emailDomains?: string[];
}

export interface JobRequestDaySchedule {
  enabled: boolean;
  timeRanges: JobRequestTimeRange[];
}

export interface JobRequestTimeRange {
  id: string;
  // format: HH:MM
  timeFrom?: string;
  // format: HH:MM
  timeTo?: string;
}

export type ClientAdfsConfig = Omit<AdfsConfig, 'clientSecret'>;

export type ClientThirdPartyLoginConfig = {
  msalConfig?: MsalConfig;
  adfsConfig?: ClientAdfsConfig;
};

export type PerigonMappings = {
  perigonClientsByDepartmentId: {
    [key: string]: { id: number };
  };
  perigonTaskMappings: PerigonTaskMapping[];
};

export type PerigonTaskMapping = {
  id: string;
  perigonTaskId: string;
  departmentId: string;
  shiftId: string;
  certificates: MappedCertificate[];
};
